/**
 * L'éditeur d'objectif, EN FENÊTRE — chantier « manuel vivant », Φ-MV-5
 * (MV-TUT-12, MV-OBJ-01, MV-OBJ-02, F-14, F-11).
 *
 * Le tuteur pose ou modifie l'objectif de l'élève : le chapitre que sa classe
 * voit, ses dates, ses évaluations, les notions à approfondir — et le réglage
 * facultatif des NIVEAUX (F-14 : tout est ouvert par défaut ; le tuteur peut
 * limiter jusqu'à un niveau).
 *
 * ─── Ce qui part au serveur ─────────────────────────────────────────────────
 *
 *  - pas d'objectif, ou un autre chapitre choisi : `POST …/eleves/:id/objectif`
 *    (un objectif actif de la même matière est clos dans la même transaction) ;
 *  - sinon : `PATCH …/objectifs/:id` — l'auteur d'origine n'est pas réécrit, un
 *    objectif posé par l'élève reste « défini par l'élève » ;
 *  - puis, SI le réglage a changé : `PUT …/niveau-maximal` (`null` le lève).
 *
 * Les règles sont dites UNE PAR UNE avant l'envoi (`reglesDObjectif`) ; le
 * serveur revérifie tout, et sa raison s'affiche telle quelle (422
 * `OBJECTIF_INVALIDE`). Rien n'est fermé tant que tout n'est pas enregistré.
 *
 * ─── D'où viennent les chapitres et les notions ─────────────────────────────
 *
 * De la lecture du programme que la console fait déjà (`programmeChapitres`,
 * `programmeChapitre`) : les chapitres PUBLIÉS de la matière et du niveau de
 * l'élève. Changer de chapitre vide les évaluations et les notions ciblées —
 * elles désignaient les notions de l'autre.
 */
import {
  LIBELLES_NATURE_EVALUATION,
  MAX_CARACTERES_TITRE_EVALUATION,
  MAX_EVALUATIONS_OBJECTIF,
  NATURES_EVALUATION_CLASSE,
  chapitreDeTableDesMatieres,
  savoirConvientA,
  type NatureEvaluationClasse,
  type VueEleveTuteurResponse,
} from '@silex/shared';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { api } from '../../api/client';
import { Chargement, ErreurRessource } from '../../components/EtatRessource';
import { Fenetre } from '../../components/Fenetre';
import {
  brouillonDObjectif,
  choixDeNiveauMaximal,
  corpsModifierObjectif,
  corpsPoserObjectif,
  evaluationDeFin,
  reglesDObjectif,
  type BrouillonEvaluation,
  type BrouillonObjectif,
} from '../../lib/manuel-tuteur-format';
import { rangNotion } from '../../lib/programme-format';
import { messageErreur, useAsync } from '../../lib/useAsync';

export function EditeurObjectif({
  vue,
  surFermeture,
  surEnregistre,
}: {
  readonly vue: VueEleveTuteurResponse;
  readonly surFermeture: () => void;
  readonly surEnregistre: () => void;
}) {
  const id = useId();
  const [brouillon, setBrouillon] = useState<BrouillonObjectif>(() => brouillonDObjectif(vue));
  const [manques, setManques] = useState<readonly string[]>([]);
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [suite, setSuite] = useState(0);

  /* Le corps de la fenêtre défile : un refus écrit en bas resterait hors de vue
     sous un objectif à plusieurs évaluations. On l'amène sous les yeux. */
  const alerte = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (manques.length > 0 || refus !== null) alerte.current?.scrollIntoView({ block: 'nearest' });
  }, [manques, refus]);

  const chapitres = useAsync(useMemo(() => () => api.programmeChapitres(), []));
  const codeChapitre = brouillon.chapitreCode;
  const chapitre = useAsync(
    useMemo(() => () => api.programmeChapitre(codeChapitre), [codeChapitre]),
  );

  const offerts =
    chapitres.status === 'ready'
      ? chapitres.data.chapitres.filter(
          (c) =>
            c.statut === 'publie' &&
            c.matiere === vue.chapitre.matiere &&
            c.niveau === vue.chapitre.niveau &&
            /* Un socle de prérequis partagés n'est le chapitre d'aucune classe :
               on n'y pose pas d'objectif (`CAP-01` ; le serveur le refuse aussi). */
            chapitreDeTableDesMatieres(c) &&
            /* Un chapitre propre à une autre séquence de mathématiques n'est pas
               celui de cet élève (`CAP-13` ; le serveur le refuse aussi). Sans
               séquence renseignée, rien n'est écarté (F-69). */
            savoirConvientA(c.sequences, vue.eleve.sequenceMaths),
        )
      : [];
  const notions = chapitre.status === 'ready' ? chapitre.data.notions : [];
  const rangChapitre =
    chapitre.status === 'ready' ? chapitre.data.chapitre.rang : vue.chapitre.rang;

  const changer = (morceau: Partial<BrouillonObjectif>): void =>
    setBrouillon((avant) => ({ ...avant, ...morceau }));
  const changerEvaluation = (cle: string, morceau: Partial<BrouillonEvaluation>): void =>
    changer({
      evaluations: brouillon.evaluations.map((e) => (e.cle === cle ? { ...e, ...morceau } : e)),
    });
  const basculer = (liste: readonly string[], code: string): string[] =>
    liste.includes(code) ? liste.filter((c) => c !== code) : [...liste, code];

  const enregistrer = async (): Promise<void> => {
    const regles = reglesDObjectif(brouillon);
    setManques(regles);
    setRefus(null);
    if (regles.length > 0) return;
    setEnvoi(true);
    try {
      const { objectif } = vue;
      if (objectif === null || objectif.chapitre.code !== brouillon.chapitreCode) {
        await api.manuelPoserObjectif(vue.eleve.id, corpsPoserObjectif(brouillon));
      } else {
        await api.manuelModifierObjectif(objectif.id, corpsModifierObjectif(brouillon));
      }
      const regle =
        vue.niveauMaximal !== null && vue.niveauMaximal.chapitre.code === brouillon.chapitreCode
          ? vue.niveauMaximal.niveauMaximal
          : null;
      if (regle !== brouillon.niveauMaximal) {
        await api.manuelReglerNiveauMaximal(vue.eleve.id, {
          chapitreCode: brouillon.chapitreCode,
          niveauMaximal: brouillon.niveauMaximal,
        });
      }
      surEnregistre();
    } catch (err) {
      setRefus(messageErreur(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <Fenetre
      titre={`Objectif de ${vue.eleve.prenom}`}
      sous="Le chapitre que sa classe voit, ses dates et ses évaluations."
      large
      testId="objectif-fenetre"
      surFermeture={surFermeture}
      actions={
        <>
          <button type="button" className="btn btn--ghost" onClick={surFermeture}>
            Annuler
          </button>
          <button
            type="button"
            className="btn btn--primary"
            disabled={envoi}
            onClick={() => void enregistrer()}
            data-testid="objectif-enregistrer"
          >
            {envoi ? 'Enregistrement…' : 'Enregistrer l’objectif'}
          </button>
        </>
      }
    >
      {chapitres.status === 'error' ? <ErreurRessource message={chapitres.message} /> : null}

      <div className="form-grid">
        <div className="field">
          <label htmlFor={`${id}-chapitre`}>Chapitre</label>
          <select
            id={`${id}-chapitre`}
            value={brouillon.chapitreCode}
            onChange={(e) =>
              changer({ chapitreCode: e.target.value, evaluations: [], notionsCiblees: [] })
            }
          >
            {/* Le chapitre de la vue reste offert même si la liste tarde : le
                sélecteur ne doit jamais s'ouvrir vide. */}
            {offerts.some((c) => c.code === vue.chapitre.code) ? null : (
              <option value={vue.chapitre.code}>{vue.chapitre.titre}</option>
            )}
            {offerts.map((c) => (
              <option key={c.code} value={c.code}>
                {c.titre}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor={`${id}-debut`}>Début en classe</label>
          <input
            id={`${id}-debut`}
            type="date"
            value={brouillon.debut}
            onChange={(e) => changer({ debut: e.target.value })}
          />
        </div>
        <div className="field">
          <label htmlFor={`${id}-fin`}>Fin en classe</label>
          <input
            id={`${id}-fin`}
            type="date"
            value={brouillon.fin}
            onChange={(e) => changer({ fin: e.target.value })}
          />
        </div>
      </div>

      {/* « Niveaux », juste sous les dates (revue UX `UX-7-12`) : c'était le
          dernier réglage de la fenêtre, sous deux évaluations et douze puces. */}
      <h3 className="fenetre__section">Niveaux</h3>
      <div className="stack-sm" role="radiogroup" aria-label="Niveaux d’exercice ouverts à l’élève">
        {choixDeNiveauMaximal().map((choix) => (
          <label key={String(choix.valeur)} className="check">
            <input
              type="radio"
              name={`${id}-niveau`}
              checked={brouillon.niveauMaximal === choix.valeur}
              onChange={() => changer({ niveauMaximal: choix.valeur })}
              data-testid={`objectif-niveau-${choix.valeur ?? 'ouvert'}`}
            />
            {choix.libelle}
          </label>
        ))}
      </div>
      <p className="form-note" data-testid="objectif-niveau-effet">
        {brouillon.niveauMaximal === null
          ? `${vue.eleve.prenom} voit tous les niveaux : le moteur conseille, il ne ferme rien.`
          : `Les exercices au-delà ne sont ni listés ni proposés à ${vue.eleve.prenom}, qui le lit en une phrase.`}
      </p>

      {chapitre.status === 'loading' ? <Chargement quoi="les notions du chapitre" /> : null}
      {chapitre.status === 'error' ? <ErreurRessource message={chapitre.message} /> : null}

      <h3 className="fenetre__section">Évaluations</h3>
      {brouillon.evaluations.length === 0 ? (
        <p className="note-line">Aucune date d’évaluation saisie.</p>
      ) : null}
      {brouillon.evaluations.map((e, i) => (
        <fieldset key={e.cle} className="evaluation-saisie" data-testid="objectif-evaluation">
          <legend className="sr-only">Évaluation {i + 1}</legend>
          <div className="form-grid">
            <div className="field">
              <label htmlFor={`${id}-titre-${e.cle}`}>Intitulé</label>
              <input
                id={`${id}-titre-${e.cle}`}
                type="text"
                maxLength={MAX_CARACTERES_TITRE_EVALUATION}
                value={e.titre}
                onChange={(ev) => changerEvaluation(e.cle, { titre: ev.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`${id}-date-${e.cle}`}>Date</label>
              <input
                id={`${id}-date-${e.cle}`}
                type="date"
                value={e.date}
                onChange={(ev) => changerEvaluation(e.cle, { date: ev.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor={`${id}-nature-${e.cle}`}>Nature</label>
              <select
                id={`${id}-nature-${e.cle}`}
                value={e.nature}
                onChange={(ev) =>
                  changerEvaluation(e.cle, { nature: ev.target.value as NatureEvaluationClasse })
                }
              >
                {NATURES_EVALUATION_CLASSE.map((n) => (
                  <option key={n} value={n}>
                    {LIBELLES_NATURE_EVALUATION[n]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="field">
            <span className="lbl">Notions visées</span>
            <div
              className="filters"
              role="group"
              aria-label={`Notions visées, évaluation ${i + 1}`}
            >
              {notions.map((n) => (
                <button
                  key={n.code}
                  type="button"
                  className={e.notions.includes(n.code) ? 'chip-filter is-on' : 'chip-filter'}
                  aria-pressed={e.notions.includes(n.code)}
                  onClick={() => changerEvaluation(e.cle, { notions: basculer(e.notions, n.code) })}
                >
                  {rangNotion(rangChapitre, n.rang)} · {n.titre}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() =>
              changer({ evaluations: brouillon.evaluations.filter((x) => x.cle !== e.cle) })
            }
          >
            Retirer cette évaluation
          </button>
        </fieldset>
      ))}
      {brouillon.evaluations.length < MAX_EVALUATIONS_OBJECTIF ? (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          disabled={notions.length === 0}
          data-testid="objectif-ajouter-evaluation"
          onClick={() => {
            changer({
              evaluations: [
                ...brouillon.evaluations,
                evaluationDeFin(
                  brouillon,
                  notions.map((n) => n.code),
                  `neuve-${suite}`,
                ),
              ],
            });
            setSuite(suite + 1);
          }}
        >
          Ajouter une évaluation
        </button>
      ) : null}

      <h3 className="fenetre__section">Notions à approfondir</h3>
      <div className="filters" role="group" aria-label="Notions à approfondir">
        {notions.length === 0 ? <span className="note-line">Aucune notion à cibler.</span> : null}
        {notions.map((n) => (
          <button
            key={n.code}
            type="button"
            className={
              brouillon.notionsCiblees.includes(n.code) ? 'chip-filter is-on' : 'chip-filter'
            }
            aria-pressed={brouillon.notionsCiblees.includes(n.code)}
            data-testid="objectif-notion-ciblee"
            onClick={() => changer({ notionsCiblees: basculer(brouillon.notionsCiblees, n.code) })}
          >
            {rangNotion(rangChapitre, n.rang)} · {n.titre}
          </button>
        ))}
      </div>

      <div ref={alerte}>
        {manques.length > 0 ? (
          <div className="callout callout--rare" role="alert" data-testid="objectif-manques">
            <strong>À compléter avant d’enregistrer.</strong>
            <ul>
              {manques.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        ) : null}
        {refus !== null ? (
          <div className="callout callout--rare" role="alert" data-testid="objectif-refus">
            <strong>Objectif non enregistré.</strong> {refus}
          </div>
        ) : null}
      </div>
    </Fenetre>
  );
}
